import { Button } from '@rental/ui'
import { type FormEvent, type ReactNode, type RefObject, useId } from 'react'
import { Dialog } from './Dialog'
import { ReasonList } from './SubPanel'

/**
 * A question asked before something that cannot be undone, on `Dialog` (drawn
 * outside any form, its answer stopping there: FIX-0). The body is a form, so
 * Enter confirms once it is ready; while the answer is on its way, Esc does not
 * take the question away.
 */
export function ConfirmDialog({
  dialogRef,
  title,
  children,
  confirmLabel,
  cancelLabel,
  busy,
  ready = true,
  reasons = [],
  onConfirm,
  onClose,
  className,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  title: ReactNode
  children?: ReactNode
  confirmLabel: string
  cancelLabel: string
  busy: boolean
  /** False while the answer is incomplete: the confirm button waits. */
  ready?: boolean
  /** While it waits, why, in words: said under the buttons and by the confirm button. */
  reasons?: readonly string[]
  onConfirm: () => void
  /** Every way out (the button, Esc, a success) ends here. */
  onClose?: () => void
  className?: string
}) {
  const reasonsId = useId()
  const waiting = !ready && !busy && reasons.length > 0
  function submit(event: FormEvent) {
    event.preventDefault()
    event.stopPropagation()
    if (busy || !ready) return
    onConfirm()
  }

  return (
    <Dialog dialogRef={dialogRef} title={title} busy={busy} onClose={onClose} className={className}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {children}
        <div className="flex flex-wrap justify-end gap-3">
          {/* While the answer is on its way the question stays: its outcome, or its error, shows here. */}
          <Button
            variant="outline"
            className="rounded-sm"
            disabled={busy}
            onClick={() => dialogRef.current?.close()}
          >
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            variant="staff"
            busy={busy}
            disabled={!ready && !busy}
            aria-describedby={waiting ? reasonsId : undefined}
          >
            {confirmLabel}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={waiting ? reasons : []} />
      </form>
    </Dialog>
  )
}
